// src/components/common/UserDropdown.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { User, Settings, Trophy, LogOut, ChevronDown, Shield } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import avatarDefault from "@/assets/images/profile/avatar_photo.png";
import { getImageUrl } from "@/utils/imageUrl";

const UserDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  const handleMenuClick = (action) => {
    setIsOpen(false);
    // Handle different menu actions
    switch (action) {
      case "profile":
        // Navigate to profile page or open profile modal
        navigate("/profile");
        break;
      case "settings":
        // Navigate to settings page
        navigate("/profile/settings");
        break;
      case "achievements":
        // Navigate to achievements page
        navigate("/profile/achievements");
        break;
      default:
        break;
    }
  };

  // Get user initials for avatar (not being used currently)
  const getUserInitials = () => {
    if (!user || !user.name || typeof user.name !== "string") {
      return "U"; // fallback
    }
    const names = user.name.trim().split(" ");
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger Button */}
      <button
        onClick={toggleDropdown}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* User Avatar */}
        <img
          src={user?.avatar_url ? getImageUrl(user.avatar_url) : avatarDefault}
          alt="User Avatar"
          className="w-8 h-8 rounded-full object-cover border dark:border-gray-600"
        />

        {/* User Name */}
        <span className="hidden md:block text-gray-700 dark:text-gray-200 font-medium">
          {user?.username || "Username"}
        </span>

        {/* Chevron Icon */}
        <ChevronDown
          className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50"
          >
            {/* User Info Section */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.name || "User"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.email || "user@example.com"}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-1">
              {/* Profile */}
              <button
                onClick={() => handleMenuClick("profile")}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-3 transition-colors duration-150"
              >
                <User className="w-4 h-4" />
                <span>Profile</span>
              </button>

              {/* Achievements */}
              <button
                onClick={() => handleMenuClick("achievements")}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-3 transition-colors duration-150"
              >
                <Trophy className="w-4 h-4" />
                <span>Achievements</span>
              </button>

              {/* Admin Portal - Only for Admins */}
              {user?.role === "admin" && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/admin");
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-3 transition-colors duration-150"
                >
                  <Shield className="w-4 h-4" />
                  <span>Admin Portal</span>
                </button>
              )}

              {/* Moderator Panel - Only for Moderators */}
              {user?.role === "moderator" && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    let targetPath = "/admin"; // fallback
                    if (user?.moderator_permissions?.length > 0) {
                      const firstPerm = user.moderator_permissions[0];
                      if (firstPerm === "user_management") targetPath = "/admin/users";
                      else if (firstPerm === "route_management") targetPath = "/admin/routes";
                      else if (firstPerm === "organizer_management") targetPath = "/admin/organizers";
                      else if (firstPerm === "sponsor_management") targetPath = "/admin/sponsors";
                    }
                    navigate(targetPath);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-3 transition-colors duration-150"
                >
                  <Shield className="w-4 h-4" />
                  <span>Moderator Panel</span>
                </button>
              )}

              {/* Settings */}
              <button
                onClick={() => handleMenuClick("settings")}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-3 transition-colors duration-150"
              >
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </button>

              {/* Divider */}
              <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-3 transition-colors duration-150"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserDropdown;
